// Authentication service for managing JWT tokens

import { API_CONFIG } from '../utils/config';

const TOKEN_KEY = API_CONFIG.tokenKey;

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
}

interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  roles?: string;
}

export const authService = {
  // Get the stored token
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Store the token
  setToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  },

  // Remove the token (logout)
  removeToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  },

  // Decode JWT payload without verification (just to read exp claim)
  decodeToken(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      // Decode the payload (second part)
      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodedPayload) as JWTPayload;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  },

  // Check if token is expired
  isTokenExpired(token: string | null): boolean {
    if (!token) {
      return true;
    }

    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return true;
    }

    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    const currentTime = Date.now();
    
    // Check if token is expired (with 1 second buffer to account for clock skew)
    return currentTime >= expirationTime - 1000;
  },

  // Check if user is authenticated and token is not expired
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return !this.isTokenExpired(token);
  },

  // Get token expiration time (in milliseconds)
  getTokenExpiration(token: string | null): number | null {
    if (!token) {
      return null;
    }

    const payload = this.decodeToken(token);
    if (!payload || !payload.exp) {
      return null;
    }

    // exp is in seconds, convert to milliseconds
    return payload.exp * 1000;
  },

  // Get valid token or throw error if not authenticated or expired
  getValidToken(): string {
    const token = this.getToken();
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    if (this.isTokenExpired(token)) {
      this.removeToken();
      throw new Error('Session expired. Please log in again.');
    }

    return token;
  },

  // Login API call
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${API_CONFIG.backendUrl}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const errorMessage = await response.text();
      throw new Error(errorMessage || 'Login failed');
    }

    const data: LoginResponse = await response.json();
    return data;
  },
};

